/**
 * useAssetThumb - a library card's picture as a data URL, or null while there
 * is none (the card shows its drawing of the kind). This browser's cache
 * first; the server once, when the cache misses. Read only for a card that is
 * mounted, never for the whole library.
 */
import { useEffect, useState } from 'react';
import { readThumb } from '../assetStore';
import { cachedThumb, cacheThumb } from '../utils/thumbCache';
import { diag, errorSummary } from '../diagnostics/log';
import type { AssetRow } from '../engine/assets/types';

async function load(uid: string, assetId: string, thumbVersion: number): Promise<string | null> {
  const key = `${uid}/${assetId}@${thumbVersion}`;
  const cached = await cachedThumb(key);
  if (cached) return cached;
  const fresh = await readThumb(uid, assetId);
  if (fresh) await cacheThumb(key, fresh);
  return fresh;
}

export default function useAssetThumb(uid: string, asset: AssetRow): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const { id, thumbVersion } = asset;
  useEffect(() => {
    if (!thumbVersion) return undefined;
    let live = true;
    load(uid, id, thumbVersion)
      .then((u) => live && setUrl(u))
      .catch((err) => diag.warn('assets', 'a card picture would not load', errorSummary(err)));
    return () => {
      live = false;
    };
  }, [uid, id, thumbVersion]);
  return thumbVersion ? url : null;
}
